import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { api } from "@config/axios/axios.config";
import { SERVER_ROUTES } from "@constants/api.routes";
import { HTTP_STATUS } from "@constants/http.status.code";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { useNotificationStack } from "@libs/notistack/useNotificationStack";
import { resolveAuthFormErrorMessage } from "@utils/errorHandler";
import useChangePassword from "../hooks/useChangePassword";
import type { ChangePasswordForm, ChangePasswordPayload, ChangePasswordResponse } from "../types";

const useChangePasswordMutate = (onSuccessCallback?: () => void) => {
	const { t } = useTranslation();
	const { showSnackbar } = useNotificationStack();
	const { methods } = useChangePassword();

	const changePasswordMutation = useMutation({
		mutationFn: async (payload: ChangePasswordPayload): Promise<ChangePasswordResponse> => {
			const response = await api.post<ChangePasswordResponse>(SERVER_ROUTES.AUTH.CHANGE_PASSWORD, payload);
			return response as unknown as ChangePasswordResponse;
		},
		onSuccess: (response: ChangePasswordResponse) => {
			if (response?.statusCode === HTTP_STATUS.OK || response?.statusCode === HTTP_STATUS.CREATED) {
				showSnackbar({
					message: response?.message || t("psdaCommon.profile.changePassword.success"),
					severity: SNACKBAR_SEVERITY.SUCCESS,
				});
				methods.reset();
				onSuccessCallback?.();
			}
		},
		onError: (error: AxiosError) => {
			methods.setError("root", {
				type: "manual",
				message: resolveAuthFormErrorMessage(error, {
					generic: t("psdaCommon.messages.error.api.genericError"),
					server: t("psdaCommon.messages.error.api.serverError"),
					network: t("psdaCommon.messages.error.api.networkError"),
				}),
			});
		},
	});

	const onSubmit = (data: ChangePasswordForm) => {
		changePasswordMutation.mutate({
			oldPassword: data.oldPassword,
			newPassword: data.newPassword,
			confirmPassword: data.confirmPassword,
		});
	};

	return {
		methods,
		onSubmit,
		changePasswordMutation,
	};
};

export default useChangePasswordMutate;
